import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import { Download, AlertTriangle } from 'lucide-react';
import SortableHeader, { useSort } from './SortableHeader';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const WIPReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportService.getWIP();
      setData(response.data);
    } catch (err) {
      console.error('Error fetching WIP:', err);
      setError('Failed to load WIP report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('total_wip', 'desc');

  const handleExportCSV = () => {
    reportService.downloadCSV(reportService.getWIP, {}, 'wip_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-lg" />)}
        </div>
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Work in Progress</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Unbilled Time</p>
          <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.total_unbilled_time || 0)}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Unbilled Expenses</p>
          <p className="text-lg font-bold text-purple-700">{formatCurrency(summary.total_unbilled_expenses || 0)}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total WIP</p>
          <p className="text-lg font-bold text-yellow-700">{formatCurrency(summary.total_wip || 0)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Cases with WIP</p>
          <p className="text-lg font-bold text-gray-900">{summary.case_count || 0}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Case" sortKey="case_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Attorney" sortKey="attorney_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Hours" sortKey="unbilled_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Time ($)" sortKey="unbilled_time_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Expenses ($)" sortKey="unbilled_expense_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Total WIP" sortKey="total_wip" sortConfig={sortConfig} onSort={requestSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No unbilled work in progress</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.case_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{item.case_number}</div>
                    <div className="text-xs text-gray-500">{item.case_title}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.client_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.attorney_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.unbilled_hours).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.unbilled_time_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.unbilled_expense_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.total_wip)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WIPReport;
